import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  autoUpdate,
  arrow,
  flip,
  offset,
  shift,
  useFloating,
  type Placement
} from "@floating-ui/react";
import { Button, Card, Flex, Text, usePortalContainer } from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import { useGuideStep } from "./GuideStepContext";
import cx from "classnames";

const ARROW_SIZE = 12;
const STATIC_SIDES = { top: "bottom", right: "left", bottom: "top", left: "right" } as const;

export interface GuidePopoverProps {
  children: React.ReactNode;
  anchor: string;
  placement?: Placement;
  dismissLabel?: string;
}

const GuidePopover: React.FC<GuidePopoverProps> = ({
  children,
  anchor,
  placement: requestedPlacement = "bottom",
  dismissLabel
}) => {
  const { guideId, stepIndex, onComplete, onDismiss, onNext, onPrev, stepIndicator, isLast } =
    useGuideStep();
  const portalContainer = usePortalContainer();
  const arrowRef = useRef<HTMLDivElement>(null);
  const [hasTarget, setHasTarget] = useState(false);
  const { refs, floatingStyles, middlewareData, placement, isPositioned } = useFloating({
    strategy: "fixed",
    placement: requestedPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(ARROW_SIZE),
      flip({ padding: ARROW_SIZE }),
      shift({ padding: ARROW_SIZE }),
      arrow({ element: arrowRef, padding: 8 }) // 8 = card border-radius
    ]
  });

  const side = placement.split("-")[0] as keyof typeof STATIC_SIDES;
  const staticSide = STATIC_SIDES[side];

  useEffect(() => {
    let tracked = document.querySelector(anchor) as Element | null;
    setHasTarget(!!tracked);
    if (tracked) refs.setReference(tracked);

    const observer = new MutationObserver(() => {
      if (tracked) {
        // Fast path: check if the known element left
        if (!document.contains(tracked)) {
          tracked = null;
          setHasTarget(false);
          refs.setReference(null);
        }
      } else {
        // Anchor is missing — check if it's back
        const found = document.querySelector(anchor);
        if (found) {
          tracked = found;
          setHasTarget(true);
          refs.setReference(found);
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [anchor]); // eslint-disable-line react-hooks/exhaustive-deps -- refs is stable

  // 1px of base tucked behind the card edge to seal the seam; entire triangle is outside the card
  const arrowStyle: React.CSSProperties = {
    ...(middlewareData.arrow?.x != null ? { left: middlewareData.arrow.x } : {}),
    ...(middlewareData.arrow?.y != null ? { top: middlewareData.arrow.y } : {}),
    [staticSide]: -(ARROW_SIZE - 1)
  };

  const popover = (
    <div
      ref={refs.setFloating}
      className="oc-native-guide-popover-outer"
      data-testid={`native-guide-${guideId}-popover-step-${stepIndex}`}
      data-placement={placement}
      style={{ ...floatingStyles, visibility: isPositioned && hasTarget ? "visible" : "hidden" }}
    >
      <div ref={arrowRef} className="oc-native-guide-popover--arrow" style={arrowStyle} />
      <Card className="oc-native-guide-popover">
        {children}
        <Flex justifyContent="space-between">
          <Button
            className="oc-native-guide-popover-button"
            dataTestId={`native-guide-${guideId}-popover-step-${stepIndex}-dismiss`}
            modifier="primary"
            onClick={isLast ? onComplete : onDismiss}
          >
            {dismissLabel ?? appstr("Dismiss")}
          </Button>
          <Flex alignItems="center" gap="8px">
            <Button
              className={cx("oc-native-guide-popover-button", !onPrev && "oc-visibility-hidden")}
              dataTestId={`native-guide-${guideId}-popover-step-${stepIndex}-prev`}
              modifier="primary"
              onClick={onPrev}
              iconOnly
              iconProps={{ name: "angle-left" }}
              aria-label="Previous step"
            />
            <Text
              colorModifier="white"
              dataTestId={`native-guide-${guideId}-popover-step-indicator`}
            >
              {stepIndicator}
            </Text>
            <Button
              className={cx("oc-native-guide-popover-button", !onNext && "oc-visibility-hidden")}
              dataTestId={`native-guide-${guideId}-popover-step-${stepIndex}-next`}
              modifier="primary"
              onClick={onNext}
              iconOnly
              iconProps={{ name: "angle-right" }}
              aria-label="Next step"
            />
          </Flex>
        </Flex>
      </Card>
    </div>
  );

  return createPortal(popover, portalContainer ?? document.body);
};

export default GuidePopover;
